import { ApiError, type ErrorCode } from "@finances/shared";
import type { z } from "zod";

/**
 * The one place the client talks to the server.
 *
 * Components never call `fetch`. Every response is parsed by the same Zod
 * schema the API validated it with, so a field that quietly changed shape
 * fails here — loudly, in one place — rather than three screens away as
 * `undefined` inside a total.
 */

export class RequestFailed extends Error {
  constructor(
    readonly code: ErrorCode | "network" | "unreadable",
    /** Written for a person by the API, and safe to render unmodified. */
    message: string,
    readonly status: number,
    readonly details?: Array<{ path: Array<string | number>; message: string }>,
  ) {
    super(message);
    this.name = "RequestFailed";
  }

  /** The one worth handling specially: it means "sign in again". */
  get isUnauthenticated(): boolean {
    return this.code === "unauthenticated";
  }
}

interface Options {
  /** `PUT` only where the resource is singular per parent — docs/06 §6.1. */
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

async function raw(path: string, options: Options = {}): Promise<Response> {
  const { method = "GET", body, signal } = options;

  try {
    return await fetch(path, {
      method,
      // Same origin, so the session cookie travels without CORS.
      credentials: "same-origin",
      headers: body instanceof FormData ? {} : { "content-type": "application/json" },
      ...(body === undefined
        ? {}
        : { body: body instanceof FormData ? body : JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new RequestFailed(
      "network",
      "Could not reach the server. Check your connection and try again.",
      0,
    );
  }
}

async function readError(response: Response): Promise<RequestFailed> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new RequestFailed(
      "unreadable",
      "Something went wrong at our end. The error has been logged.",
      response.status,
    );
  }

  const parsed = ApiError.safeParse(body);
  if (!parsed.success) {
    return new RequestFailed(
      "unreadable",
      "Something went wrong at our end. The error has been logged.",
      response.status,
    );
  }

  return new RequestFailed(
    parsed.data.error.code,
    parsed.data.error.message,
    response.status,
    parsed.data.error.details,
  );
}

export async function request<S extends z.ZodType>(
  schema: S,
  path: string,
  options: Options = {},
): Promise<z.infer<S>> {
  const response = await raw(path, options);
  if (!response.ok) throw await readError(response);

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    /**
     * The server and the client share one schema, so this is a bug rather than
     * a bad input — and a silent mismatch in a finance app ends up in a total.
     *
     * The fields are logged because the message cannot name them: it is shown
     * to whoever is looking at their money, and "createdAt: invalid datetime"
     * is not for them. Without this, diagnosing it means querying the
     * database, which is how the first one went.
     */
    console.error("Response did not match its schema", {
      path,
      fields: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
    });

    throw new RequestFailed(
      "unreadable",
      "The server sent something this version of the app cannot read. Reload the page.",
      response.status,
    );
  }

  return parsed.data;
}

/** For the handful of endpoints whose reply is an acknowledgement, not data. */
export async function send(path: string, options: Options = {}): Promise<void> {
  const response = await raw(path, options);
  if (!response.ok) throw await readError(response);
}

/**
 * Better Auth owns sign-in, sign-up and reset. It is not worth a client
 * library here: these are four form posts, and the schemas are the library's
 * rather than ours.
 */
export async function auth(
  action: "sign-in/email" | "sign-up/email" | "sign-out" | "request-password-reset",
  body: Record<string, unknown>,
): Promise<void> {
  const response = await raw(`/api/auth/${action}`, { method: "POST", body });
  if (response.ok) return;

  let message = "That did not work. Try again.";
  try {
    const problem = (await response.json()) as { message?: string };
    if (problem.message) message = problem.message;
  } catch {
    // Keep the generic message: an unreadable auth error is still an error.
  }

  throw new RequestFailed(
    response.status === 401 ? "unauthenticated" : "validation_failed",
    message,
    response.status,
  );
}

export type QueryParams = Record<string, string | number | boolean | string[] | undefined>;

export function query(params: QueryParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    /* A list travels as one comma-separated value rather than as a repeated
       key, which is what `IdList` reads on the other side. An empty list is
       the same as not asking, so it leaves no parameter behind. */
    if (Array.isArray(value)) {
      if (value.length > 0) search.set(key, value.join(","));
      continue;
    }
    search.set(key, String(value));
  }
  const text = search.toString();
  return text === "" ? "" : `?${text}`;
}
